import { useIntl } from 'react-intl'

/**
 * Returns a localised property category label for homes & villas.
 *
 * To add a new prefixed category, add the ENTIRE translated phrase
 * (e.g. "Entire treehouse") as a new `case` below.
 * Do NOT try to concatenate a translated "Entire" prefix with the category —
 * other languages have different grammar, word order, and gender agreement
 * that make prefix concatenation unreliable.
 */
function usePropertyCategory(category?: string): string {
  const intl = useIntl()

  switch (category) {
    case 'Cabin':
      return intl.formatMessage({
        id: 'L6hwAU1MBcxdNtPKVO60',
        defaultMessage: 'Entire cabin',
        description: 'Property category label for an entire cabin rental',
      })
    case 'Campsite':
      return intl.formatMessage({
        id: '7mtGZE3Y6ZmIy3msqoUk',
        defaultMessage: 'Entire campsite',
        description: 'Property category label for an entire campsite rental',
      })
    case 'Chalet':
      return intl.formatMessage({
        id: 'T-wkH3VqFTygg5PiEGZ4',
        defaultMessage: 'Entire chalet',
        description: 'Property category label for an entire chalet rental',
      })
    case 'Condominium':
      return intl.formatMessage({
        id: '1vgTPLsv2-mxp7wxfUlH',
        defaultMessage: 'Entire condominium',
        description: 'Property category label for an entire condominium rental',
      })
    case 'Country House':
      return intl.formatMessage({
        id: 'YvhfHB0pWF4UunoavJsV',
        defaultMessage: 'Entire country house',
        description: 'Property category label for an entire country house rental',
      })
    case 'Guesthouse':
      return intl.formatMessage({
        id: 'gcrSayDt9UGIZ6mEx1i7',
        defaultMessage: 'Entire guesthouse',
        description: 'Property category label for an entire guesthouse rental',
      })
    case 'Apartment':
      return intl.formatMessage({
        id: 'BOLOk_kBxWInBqXTLV84',
        defaultMessage: 'Entire apartment',
        description: 'Property category label for an entire apartment rental',
      })
    case 'Private vacation home':
      return intl.formatMessage({
        id: 'FC2DdBGOaRRuz1EIAD-J',
        defaultMessage: 'Entire private vacation home',
        description: 'Property category label for an entire private vacation home rental',
      })
    case 'Villa':
      return intl.formatMessage({
        id: 'nPwjbi9wGUIlsOB87O2C',
        defaultMessage: 'Entire villa',
        description: 'Property category label for an entire villa rental',
      })
    case 'Castle':
      return intl.formatMessage({
        id: 'mV37fLXSekwoMzoPTYtZ',
        defaultMessage: 'Entire castle',
        description: 'Property category label for an entire castle rental',
      })
    default:
      if (category) return category
      return intl.formatMessage({
        id: 'bWZRSRX8cyVWjqjZss6_',
        defaultMessage: 'Entire villa',
        description: 'Default property category label when no category is specified',
      })
  }
}

export default usePropertyCategory
